/**
 * Over each figure: speech bubbles, emoji, titles, names, karma, stance and status badges, health
 * bars and quest marks; the warp circles' destinations; and the damage numbers and move callouts.
 * Drawn on the screen in the zoomed world's transform, so they follow the zoom.
 */
import { threatColor } from '../threatColor';
import { clamp, EMOJI_SECONDS, monsterRank, npcMark, rankFor, rankName, threat, TITLES, ZONES } from '@nine/shared';
import { plateClock } from '../../plates/motion';
import { settings } from '../../settings';
import { CROWD, hidden } from '../drawKit';
import { globeBend } from '../globe';
import { drawEmojiBubble } from '../emotes';
import {
  bubble,
  callout,
  personLabels,
  PIXEL,
  risingGlyphs,
  stanceIcon,
  statusIcons,
  tag,
  UI,
} from '../labels';
import { visualScale } from '../sprites';
import { duelRing } from './duelRing';
import { registerRenderPass } from './registry';

registerRenderPass({
  id: 'labels',
  layer: 'labels',
  order: 100,
  draw(f) {
    const { mg, world: w, px: PX, effects: fx, sprites, iso, ox, oy } = f;
    mg.textAlign = 'center';
    mg.lineJoin = 'round';
    mg.lineWidth = 3.5;
    mg.strokeStyle = 'rgba(12,9,14,.92)';

    const ambientLabels = new Set<string>();
    const clock = plateClock(w.time);
    // Where each warp circle leads (and how strong its monsters are).
    // In a bout the tags thin to the two fighters (render/passes/duelRing.ts): nothing else competes for the eye.
    for (const P of duelRing.on ? [] : w.map.portals) {
      const [px, py] = iso.proj(P.x, P.y);
      const z = ZONES[P.to];
      tag(mg, `◈ ${z.name} · ${z.levels}`, (px + ox) * PX, globeBend(px + ox, py + oy - 70)[1] * PX, `600 13px ${UI}`, '#9fd8ff');
    }
    for (const e of f.shown()) {
      if (duelRing.on && e.id !== duelRing.me && e.id !== duelRing.foe) continue;
      if (e.kind === 'pet' || e.critter || hidden(e) || (e.kind === 'monster' && e.dead)) continue;
      if (w.carriage.aboard().includes(e.id)) continue;
      const set = sprites.of(e);
      if (!set) continue;
      const [sx, sy] = iso.proj(e.x, e.y);
      const x = (sx + ox) * PX;
      // through the Globe View's bend (globe.ts; flat when it is off)
      const top =
        globeBend(sx + ox, sy + oy - e.z - fx.hoverOf(e) - fx.anim(e.id).elev - set.h * visualScale(e, set) - 3)[1] * PX;
      if (x < -80 || x > f.screenW + 80 || top < -60 || top > f.screenH + 40) continue;
      const said = fx.bubbles.get(e.id);
      if (said) bubble(mg, said.text, said.t, x, top - (e.kind === 'npc' ? 46 : 26));
      if (e.emoji && !e.dead) {
        // An emoji over the head, above the name and title (drawn in art pixels, scaled up).
        mg.save();
        mg.scale(PX, PX);
        drawEmojiBubble(mg, e.emoji, x / PX, (top - 26) / PX, EMOJI_SECONDS - e.emojiT, e.emojiT);
        mg.restore();
      }

      if (e.kind === 'player' || e.kind === 'rival') {
        // A player, or another player: title (plate), name, karma, stance (render/labels.ts).
        if (e.dead) continue;
        const worn = TITLES[w.progress.titleWorn];
        if (e.kind === 'player' && worn?.aura?.style === 'swordink')
          risingGlyphs(mg, w.time, PX, fx, e, x, (sy + oy) * PX);
        personLabels(mg, w, e, x, top, f.crowd, clock);
      } else if (e.kind === 'ambient') {
        // Dense civilian crowds share a label cell so names stay readable; the selected
        // character and speech bubbles always remain visible. Bodies keep full detail.
        if (f.crowd >= CROWD && e.id !== w.player.targetId) {
          const cell = `${Math.floor(x / 100)},${Math.floor(top / 36)}`;
          if (ambientLabels.has(cell)) continue;
          ambientLabels.add(cell);
        }
        tag(mg, e.title, x, top - 17, `400 11px ${UI}`, '#c8b6ff');
        tag(mg, e.name, x, top - 2, `500 13px ${UI}`, '#f4f0ff');
      } else if (e.kind === 'npc') {
        tag(mg, e.title, x, top - 17, `400 11px ${UI}`, '#b9ae98');
        tag(mg, e.name, x, top - 2, `500 14px ${UI}`, '#9fdcff');
        const bob = top - 34 + Math.sin(w.time * 4) * 3;
        // A quest to take (!) or hand in (?) in gold, a school recruiter in blue (shared systems/plainsStory.ts npcMark).
        const mark = npcMark(w, e);
        if (mark === 'offer' || mark === 'ready') tag(mg, mark === 'offer' ? '!' : '?', x, bob, `700 26px ${PIXEL}`, '#ffd45a');
        else if (mark === 'recruit') tag(mg, '!', x, bob, `700 26px ${PIXEL}`, '#8fe0ff');
      } else {
        const boss = e.brain?.defId.endsWith('boss');
        const chasing = e.brain?.state === 'chase';
        // Its power rank before its name, coloured by how dangerous it is next to the player:
        // grey well below, white about even, red above.
        const rank = monsterRank(e.level);
        const danger = threat(rank, rankFor(w.progress.cultivationSpent).index);
        tag(
          mg,
          `${rankName(rank)} · ${e.name}`,
          x,
          top - (boss ? 8 : 4),
          boss ? `700 16px ${UI}` : `500 12px ${UI}`,
          danger === 'even' && chasing ? '#ffd8c8' : threatColor(rank, rankFor(w.progress.cultivationSpent).index),
        );
        if (e.hp < e.maxHp || w.player.targetId === e.id) {
          const bw = boss ? 70 : 36;
          const by = top + (boss ? -2 : 1);
          mg.fillStyle = 'rgba(10,8,12,.85)';
          mg.fillRect(x - bw / 2 - 1, by, bw + 2, 5);
          mg.fillStyle = boss ? '#c0304a' : '#d9483c';
          mg.fillRect(x - bw / 2, by + 1, bw * clamp(e.hp / e.maxHp, 0, 1), 3);
        }
        stanceIcon(mg, e, x, top - (boss ? 34 : 28));
        statusIcons(mg, e, x, top - (boss ? 14 : 18));
        if (e.stunT > 0) tag(mg, 'มึนงง', x, top - (boss ? 26 : 18), `600 11px ${UI}`, '#ffe36a');
      }
    }

    for (const n of fx.texts) {
      if (!n.glyph && !settings.damageNumbers) continue;
      if (n.glyph) {
        callout(mg, PX, n, ox, oy);
        continue;
      }
      const k = n.t / n.life;
      const x = (n.x + ox) * PX;
      // In a bout the numbers rise above the name tags (title + name stand ~34 px over the head), not through them.
      const y = (n.y + oy) * PX - k * 42 - (duelRing.on ? 40 : 0);
      const pop = n.t < 0.12 ? 1 + (0.12 - n.t) * 4 : 1;
      mg.globalAlpha = k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
      tag(mg, n.text, x, y, `700 ${Math.round(n.size * pop)}px ${PIXEL}`, n.color);
    }
    mg.globalAlpha = 1;
  },
});
